import { Application } from 'pixi.js';
import type { Scene } from './Scene';
import { getTelegramAdapter } from '../telegram';
import { Time } from './Time';
import { InputSystem } from '../systems/InputSystem';
import { Camera } from './Camera';
import { EntityManager } from './EntityManager';
import { AudioSystem } from './Audio';
import { gameEvents } from './EventBus';
import { perfMonitor } from './Performance';
import { errorTracker } from './ErrorTracker';
import { a11y } from './Accessibility';
import { config } from '../config/env';
import { preloadAllSprites } from '../content/SpriteCache';

export interface GameConfig {
  container: HTMLElement;
  width: number;
  height: number;
  scenes: Record<string, new (game: Game) => Scene>;
  initialScene: string;
}

export class Game {
  readonly app: Application;
  readonly scenes: Map<string, Scene> = new Map();
  private currentScene: Scene | null = null;
  readonly telegram = getTelegramAdapter();

  readonly time = new Time();
  input: InputSystem | null = null;
  readonly camera: Camera;
  readonly entities = new EntityManager();
  readonly audio = new AudioSystem();
  readonly events = gameEvents;

  private paused = false;
  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private config: GameConfig) {
    this.app = new Application();
    this.camera = new Camera(config.width, config.height);
  }

  async start(): Promise<void> {
    // Init error tracking
    errorTracker.init(config.sentryDsn ?? undefined);

    // Init accessibility
    a11y.init();

    // Use full window size - Telegram header is part of the browser chrome
    await this.app.init({
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: 0x1a1a2e,
      antialias: false,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    if (!this.app.canvas) {
      throw new Error('PixiJS did not create a canvas');
    }

    this.config.container.appendChild(this.app.canvas);

    // Add camera container to stage
    this.app.stage.addChild(this.camera.container);

    // Preload all sprites before creating scenes
    console.log('[Game] Preloading sprites...');
    await preloadAllSprites();
    console.log('[Game] Sprites preloaded');

    this.input = new InputSystem(this.camera.container);
    this.input.setApp(this.app);

    this.setupVisibilityHandler();
    this.setupResizeHandler();

    for (const [name, SceneClass] of Object.entries(this.config.scenes)) {
      const scene = new SceneClass(this);
      this.scenes.set(name, scene);
    }

    this.telegram.disableVerticalSwipe();
    this.setupFullscreen();
    this.switchScene('boot');

    this.time.start();
    this.app.ticker.add(this.loop, this);

    a11y.announce('Игра загружена');
  }

  private loop = (): void => {
    const steps = this.time.update();
    if (steps > 0 && !this.paused) {
      for (let i = 0; i < steps; i++) {
        this.fixedUpdate(this.time.fixedStep, this.time.tick);
      }
    }
    perfMonitor.trackFPS(this.time.fps);
  };

  private fixedUpdate(dt: number, tick: number): void {
    if (this.currentScene) {
      this.currentScene.fixedUpdate?.(dt, tick);
    }
    this.entities.update(dt, tick);
  }

  switchScene(name: string): void {
    const scene = this.scenes.get(name);
    if (!scene) throw new Error(`Scene "${name}" not found`);

    if (this.currentScene) {
      this.currentScene.exit();
      this.camera.container.removeChild(this.currentScene.container);
    }

    this.currentScene = scene;
    this.camera.container.addChild(scene.container);
    this.entities.clear();
    scene.enter();

    a11y.announce(`Сцена: ${name}`);
  }

  pause(): void {
    this.paused = true;
    this.time.pause();
  }

  resume(): void {
    this.paused = false;
    this.time.resume();
  }

  get isPaused(): boolean {
    return this.paused;
  }

  private setupVisibilityHandler(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
    });
    window.addEventListener('blur', () => this.pause());
  }

  private setupFullscreen(): void {
    // Try fullscreen immediately (some Telegram versions allow it)
    this.telegram.requestFullscreen();

    // On first user tap, try again (browsers require user gesture)
    let requested = false;
    const onFirstTap = () => {
      if (!requested && !this.telegram.isFullscreen()) {
        requested = true;
        this.telegram.requestFullscreen();
        this.telegram.lockOrientation();
      }
      document.removeEventListener('pointerdown', onFirstTap);
    };
    document.addEventListener('pointerdown', onFirstTap);

    // React to fullscreen changes
    this.telegram.onFullscreenChange(() => {
      console.log('[Game] fullscreen changed:', this.telegram.isFullscreen());
      setTimeout(() => this.handleResize(), 50);
    });

    // React to orientation changes
    this.telegram.onOrientationChange((orientation) => {
      console.log('[Game] orientation changed:', orientation);
      setTimeout(() => this.handleResize(), 200);
    });
  }

  private setupResizeHandler(): void {
    const onResize = () => {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 100);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', () => {
      setTimeout(onResize, 200);
    });
  }

  private handleResize(): void {
    // Prefer Telegram viewport dimensions when available
    const w = this.telegram.getViewportWidth();
    const h = this.telegram.getViewportHeight();

    this.app.renderer.resize(w, h);
    this.camera.screenWidth = w;
    this.camera.screenHeight = h;

    if (this.input) {
      this.input.repositionButtons(w, h);
    }

    if (this.currentScene && this.currentScene.onResize) {
      this.currentScene.onResize(w, h);
    }
  }

  getScreenBounds(): { width: number; height: number; top: number; bottom: number } {
    const safeArea = this.telegram.getSafeAreaInsets();
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      top: safeArea.top + this.telegram.getHeaderHeight(),
      bottom: safeArea.bottom,
    };
  }
}
